import * as THREE from "three";
import { getActiveAdventure } from "../adventure/ActiveAdventure.js";
import {
  createSurfaceMaterial,
  type SurfaceMaterialOptions,
} from "./MaterialFactory.js";

/**
 * Per-adventure material cache (T01).
 *
 * Replaces module-level `MAT_*` singletons that used to live at the top of
 * `DungeonBuilder.ts`. Those constants were constructed at module-import
 * time, before `mountAdventure()` runs — so
 * `createSurfaceMaterial` → `attachMaps` → `worldTextureUrl(...)`
 * resolved against the **default** adventure (`bkcrawl-mvp`) regardless
 * of which adventure actually booted. Net: any non-default adventure
 * silently inherited bkcrawl-mvp textures. This cache defers material
 * construction to build time, when the active adventure is correct, and
 * keys the result on `(adventureBaseUrl, cacheKey)` so each adventure
 * gets its own set.
 *
 * T02 will extend `cacheKey` with an optional `setKey` for named
 * texture sets under `world/textures/sets/<key>/`.
 */

type SurfaceBucket = Map<string, THREE.MeshStandardMaterial>;

const cache = new Map<string, SurfaceBucket>();

/**
 * Return the cached material for `(active adventure, surface, setKey)`,
 * building it lazily on first request. The cache key is
 * `${surface}::${setKey ?? ""}` so calls that pass no set and calls
 * that pass `setKey: ""` both hit the same adventure-default material.
 */
export function getSurfaceMaterial(
  options: SurfaceMaterialOptions,
): THREE.MeshStandardMaterial {
  const baseUrl = getActiveAdventure().baseUrl;
  let bucket = cache.get(baseUrl);
  if (!bucket) {
    bucket = new Map();
    cache.set(baseUrl, bucket);
  }
  const key = `${options.surface}::${options.setKey ?? ""}`;
  let mat = bucket.get(key);
  if (!mat) {
    mat = createSurfaceMaterial(options);
    bucket.set(key, mat);
  }
  return mat;
}

/**
 * Drop every cached material. Intended for test teardown — production
 * code keeps the cache alive for the life of the page so repeat map
 * loads and adventure switch-backs reuse work.
 */
export function clearMaterialCache(): void {
  cache.clear();
}
